import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { FaRegCalendar, FaRegCalendarCheck, FaTag } from 'react-icons/fa';
import { notes, extractText, formatDate } from './notes-data';
import './PersonalNotes.scss'

const SNIPPET_CHARS = 220;

const buildSnippet = (content) => {
  const text = extractText(content).replace(/\s+/g, ' ').trim();
  if (text.length <= SNIPPET_CHARS) return { text, truncated: false };
  const cut = text.slice(0, SNIPPET_CHARS);
  const lastSpace = cut.lastIndexOf(' ');
  return { text: (lastSpace > 0 ? cut.slice(0, lastSpace) : cut) + '…', truncated: true };
};

const FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'story', label: 'Story' },
  { value: 'note', label: 'Note' },
];

const PersonalNotes = () => {
  const [filter, setFilter] = useState('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="personal-notes">
      <div className="blog-intro-header">
        <div className="blog-intro-top">
          <h3>Here, I write.</h3>
          <div className="note-filter" ref={dropdownRef}>
        <button
          className={`note-filter-btn${dropdownOpen ? ' open' : ''}${filter !== 'all' ? ' active' : ''}`}
          onClick={() => setDropdownOpen(o => !o)}
        >
          <FaTag className="note-filter-icon" />
          {FILTERS.find(f => f.value === filter)?.label}
          <svg className="note-filter-chevron" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M1 1l4 4 4-4"/></svg>
        </button>
        {dropdownOpen && (
          <div className="note-filter-dropdown">
            <div className="note-filter-dropdown-heading">Categories</div>
            {FILTERS.map(f => (
              <button
                key={f.value}
                className={`note-filter-option${filter === f.value ? ' active' : ''}`}
                onClick={() => { setFilter(f.value); setDropdownOpen(false); }}
              >
                {f.label}
              </button>
            ))}
          </div>
        )}
          </div>
        </div>
      </div>
      <ul className="note-list">
        {notes.filter(n => filter === 'all' || n.tags?.includes(filter)).map((note) => {
          const snippet = note.excerpt
            ? { text: note.excerpt, truncated: true }
            : buildSnippet(note.content);
          const fallbackTitle = snippet.text.split(/[.!?]/)[0];
          const meta = (
            <div className="note-card-top">
              <div className="note-meta">
                <span className="note-meta-item">
                  <FaRegCalendar className="note-meta-icon" />
                  {formatDate(note.date)}
                </span>
                {note.updated && (
                  <span className="note-meta-item">
                    <FaRegCalendarCheck className="note-meta-icon" />
                    {formatDate(note.updated)}
                  </span>
                )}
              </div>
              {note.tags?.length > 0 && (
                <div className="note-tags">
                  {note.tags.map(tag => (
                    <span key={tag} className={`note-tag note-tag--${tag}`}>{tag}</span>
                  ))}
                </div>
              )}
            </div>
          );
          const inner = (
            <>
              {meta}
              <h2 className="note-card-title">{note.title || fallbackTitle}</h2>
              <p className="note-card-snippet">{snippet.text}</p>
              {snippet.truncated && <span className="note-card-readmore">Read more →</span>}
            </>
          );
          return (
            <motion.li
              key={note.id}
              className={`note-card${snippet.truncated ? '' : ' note-card-short'}${note.tags?.[0] ? ` note-card--${note.tags[0]}` : ''}`}
              whileHover={snippet.truncated ? { y: -2, scale: 1.02 } : undefined}
              transition={{ duration: 0.25, ease: [0.34, 1.56, 0.64, 1] }}
            >
              {snippet.truncated ? (
                <Link to={`/blog/${note.id}`} className="note-card-link">{inner}</Link>
              ) : (
                <div className="note-card-link note-card-static">{inner}</div>
              )}
            </motion.li>
          );
        })}
      </ul>
    </div>
  );
};

export default PersonalNotes;
